import NP, {ValueChangedEventArgs} from "../../1. Core/NP";
import Control from "../../1. Core/Control";
import TopAppBar from "../../1. Core/Controls/TopAppBar/TopAppBar";
import DataTable from "../../1. Core/Controls/DataTable/DataTable";
import {ServerAPI} from "../../1. Core/ServerAPI";
import {PageMode} from "../../10. Battle/types";
import UserAchievementsPanel from "./UserAchievementsPanel";

interface PlayerAchievementsRow {
	userId: number;
	email: string;
	firstName: string;
	lastName: string;
	claimsCount: number;
	Name: string;
}

interface PlayerAchievementsListResponse {
	players: PlayerAchievementsRow[];
}

export default class PlayerAchievementsPanel extends Control {
	private _Offset: number;
	private _Count: number;
	private _FilterText: string;
	private _topAppBar: TopAppBar;
	private _list: DataTable;
	private _dataWithFiltersNode: HTMLElement;
	private _OpenedItemId: number;
	private _drillDownPanel: UserAchievementsPanel;

	constructor(settings?: object) {
		super(settings);
		this._render();
	}

	_initFields(settings: object) {
		this._OpenedItemId = null;
		super._initFields(settings);
	}

	_render() {
		this.np_render();

		this._Offset = 0;
		this._Count = 100;
		this._FilterText = '';

		this._topAppBar = new TopAppBar({
			Class: 'top-app-bar--no-shadow top-app-bar--light library-box-list__app-bar',
			Caption: 'Player Achievements',
			ParentNode: this._root,
			ShowSearchButton: true,
			EvHideSearch: () => this.filterItems(null),
			EvSearch: (s: TopAppBar, e: ValueChangedEventArgs) => this.filterItems(e.Value)
		});

		this._dataWithFiltersNode = NP.createNode(this._root, 'data-with-filters-container');

		this._list = new DataTable({
			ParentNode: this._dataWithFiltersNode,
			ShowPaginator: true,
			EnableSort: false,
			EvPaginatorLeftClick: () => {
				this._Offset -= this._Count;
				this.refresh();
			},
			EvPaginatorRightClick: () => {
				this._Offset += this._Count;
				this.refresh();
			},
			Class: 'app-list--avatars',
			ShowSettingsButton: true,
			Columns: [{
				Content: 'Id',
				PropertyContent: 'userId',
				Width: '70px'
			}, {
				Content: 'Name',
				PropertyContent: 'Name',
				Width: 'calc(100% - 70px - 120px)'
			}, {
				Content: 'Achievements',
				PropertyContent: 'claimsCount',
				Width: '120px'
			}],
			EvItemClick: (s: object, e: { Item: PlayerAchievementsRow }) => {
				this.showUserAchievements(e.Item.userId);
			}
		});

	}

	showUserAchievements(userId: number) {
		if (this._drillDownPanel) {
			if (this._drillDownPanel.getUserId() === userId) {
				return;
			}
			this._drillDownPanel.destroy();
			this._drillDownPanel = null;
		}

		NP.updateBrowserUrlString(true, PageMode.player_achievements, userId);

		this._topAppBar.setIsVisible(false);
		this._list.setIsVisible(false);

		this._drillDownPanel = new UserAchievementsPanel({
			ParentNode: this._root,
			UserId: userId,
			Width: this._Width,
			Height: this._Height - 44,
			EvClosed: () => {
				NP.updateBrowserUrlString(true, PageMode.player_achievements, null);
				this._drillDownPanel.destroy();
				this._drillDownPanel = null;
				this._OpenedItemId = null;
				this._topAppBar.setIsVisible(true);
				this._list.setIsVisible(true);
			}
		});
	}

	getTopAppBar() {
		return this._drillDownPanel?.getTopAppBar() || this._topAppBar;
	}

	setHeight(value: number) {
		super.setHeight(value);
		if (this._list) {
			this._list.setHeight(this._Height - 64);
		}
		this._drillDownPanel?.setHeight(this._Height - 64);
	}

	setWidth(value: number) {
		super.setWidth(value);
		if (this._list) {
			this._list.setWidth(value);
		}
		this._drillDownPanel?.setWidth(value);
	}

	filterItems(value: string) {
		this._FilterText = value;
		this._Offset = 0;
		this.refresh();
	}

	async refresh() {
		const res = await ServerAPI.post<PlayerAchievementsListResponse>('/achievements/v2/admin/players', {
			offset: this._Offset,
			count: this._Count,
			filterText: this._FilterText || ''
		});

		this._list.setRows(res.players.map((item) => ({
			...item,
			Name: `${item.email} ${item.firstName || ''} ${item.lastName || ''}`.trim()
		})));

		this._list.updatePagCaption(`${this._Offset + 1} - ${Math.min(this._Offset + this._Count, this._Offset + res.players.length)}`);
		this._list.getRightPagButton().setEnabled(res.players.length === this._Count);
		this._list.getLeftPagButton().setEnabled(this._Offset > 0);

		if (this._OpenedItemId != null) {
			this.showUserAchievements(this._OpenedItemId);
		}
	}
}
